import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, SurveyQuestion, SurveyRow } from '../../api';
import { surveyEndpoints } from '../../api';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, statusTone } from '../../ui';
import { INPUT, inZone } from './shared';
import type { PanelProps } from '../Event';
import { dateWords } from '../../terms';

/**
 * Surveys on this event (D-168).
 *
 * A survey is built as a draft, opened, and then read — and it cannot be
 * changed once it is open, because the answers underneath a question were
 * given to the words that were there. Opening one puts its link in the
 * post-event email in place of `survey_url`.
 *
 * Results are shown **per performance** as well as in total, since that is the
 * number worth having: a run of six nights is six different evenings, and an
 * average across all of them hides the one that went wrong.
 */

const KINDS: [SurveyQuestion['kind'], string][] = [
  ['RATING', 'Rating out of five'],
  ['SINGLE_CHOICE', 'Choose one'],
  ['MULTI_CHOICE', 'Choose any'],
  ['TEXT', 'In their own words'],
];

export function SurveysPanel({ detail, onError, can }: PanelProps) {
  const eventId = detail.event.id;
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [name, setName] = useState('How was it?');
  const surveys = useQuery({ queryKey: ['surveys', eventId], queryFn: () => surveyEndpoints.forEvent(eventId) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['surveys', eventId] });
  const create = useMutation({
    mutationFn: () => surveyEndpoints.create(eventId, { name: name.trim() }),
    onSuccess: (result) => {
      setOpenId(result.survey.id);
      refresh();
    },
    onError,
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SurveyRow['status'] }) => surveyEndpoints.setStatus(id, status),
    onSuccess: refresh,
    onError,
  });

  if (!can('surveys.manage')) {
    return (
      <Panel title="Surveys">
        <p className="px-5 py-4 text-sm text-ink-600">You do not have permission to see surveys. Ask for `surveys.manage`.</p>
      </Panel>
    );
  }

  const list = surveys.data?.surveys ?? [];
  return (
    <div className="space-y-4">
      <Panel
        title="Surveys"
        description="Asked in the post-event email, answered here rather than on somebody else's form. One survey is open at a time; its link replaces the survey URL on the event."
        actions={
          <div className="flex items-end gap-2">
            <Field label="New survey" htmlFor="survey-name">
              <input id="survey-name" className={INPUT} value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            <Button variant="primary" disabled={create.isPending || !name.trim()} onClick={() => create.mutate()}>
              Create
            </Button>
          </div>
        }
      >
        <Table headers={['Survey', 'Status', 'Questions', 'Asked', 'Answered', '']}>
          {list.length === 0 ? (
            <EmptyRow columns={6}>No surveys on this event yet.</EmptyRow>
          ) : (
            list.map((survey) => (
              <tr key={survey.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">
                  <button type="button" className="text-accent underline-offset-2 hover:underline" onClick={() => setOpenId(openId === survey.id ? null : survey.id)}>
                    {survey.name}
                  </button>
                </td>
                <td className="px-5 py-3">
                  <Badge tone={statusTone(survey.status)}>{survey.status}</Badge>
                </td>
                <td className="tabular px-5 py-3 text-sm">{survey.questions ?? 0}</td>
                <td className="tabular px-5 py-3 text-sm">{survey.invited ?? 0}</td>
                <td className="tabular px-5 py-3 text-sm">
                  {survey.answered ?? 0}
                  {(survey.invited ?? 0) > 0 ? <span className="ml-1 text-xs text-ink-400">{Math.round((100 * (survey.answered ?? 0)) / (survey.invited ?? 1))}%</span> : null}
                </td>
                <td className="px-5 py-3 text-right">
                  {survey.status === 'OPEN' ? (
                    <Button onClick={() => setStatus.mutate({ id: survey.id, status: 'CLOSED' })}>Close</Button>
                  ) : (
                    <Button variant="primary" disabled={(survey.questions ?? 0) === 0} onClick={() => setStatus.mutate({ id: survey.id, status: 'OPEN' })}>
                      Open
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {openId ? <SurveyDetail surveyId={openId} onError={onError} onChanged={refresh} /> : null}
    </div>
  );
}

function SurveyDetail({ surveyId, onError, onChanged }: { surveyId: string; onError: (error: ApiError) => void; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<SurveyQuestion['kind']>('RATING');
  const [prompt, setPrompt] = useState('');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState('');
  const detail = useQuery({ queryKey: ['survey', surveyId], queryFn: () => surveyEndpoints.detail(surveyId) });
  const results = useQuery({ queryKey: ['survey-results', surveyId], queryFn: () => surveyEndpoints.results(surveyId) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['survey', surveyId] });
    void queryClient.invalidateQueries({ queryKey: ['survey-results', surveyId] });
    onChanged();
  };
  const add = useMutation({
    mutationFn: () =>
      surveyEndpoints.addQuestion(surveyId, {
        kind,
        prompt: prompt.trim(),
        required,
        sort_order: (detail.data?.questions.length ?? 0) * 10,
        options: options
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setPrompt('');
      setOptions('');
      refresh();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: (questionId: string) => surveyEndpoints.removeQuestion(surveyId, questionId), onSuccess: refresh, onError });

  if (!detail.data) return null;
  const draft = detail.data.survey.status === 'DRAFT';
  const needsOptions = kind === 'SINGLE_CHOICE' || kind === 'MULTI_CHOICE';

  return (
    <>
      <Panel title={`Questions — ${detail.data.survey.name}`} description={draft ? 'Questions can only be changed while the survey is a draft.' : 'This survey is no longer a draft, so its questions are fixed: the answers underneath them were given to these words.'}>
        <Table headers={['Question', 'Kind', 'Required', '']}>
          {detail.data.questions.length === 0 ? (
            <EmptyRow columns={4}>No questions yet.</EmptyRow>
          ) : (
            detail.data.questions.map((question) => (
              <tr key={question.id} className="border-b border-line align-top last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">
                  {question.prompt}
                  {question.options.length > 0 ? <span className="mt-0.5 block text-xs text-ink-400">{question.options.map((option) => option.label).join(' · ')}</span> : null}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{KINDS.find(([code]) => code === question.kind)?.[1] ?? question.kind}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{question.required === 1 ? 'yes' : ''}</td>
                <td className="px-5 py-3 text-right">{draft ? <Button onClick={() => remove.mutate(question.id)}>Remove</Button> : null}</td>
              </tr>
            ))
          )}
        </Table>
        {draft ? (
          <div className="space-y-3 border-t border-line px-5 py-4">
            {add.error ? <ErrorNote>{(add.error as ApiError).message}</ErrorNote> : null}
            <div className="grid gap-3 sm:grid-cols-[200px_1fr]">
              <Field label="Kind" htmlFor="question-kind">
                <select id="question-kind" className={`${INPUT} w-full`} value={kind} onChange={(event) => setKind(event.target.value as SurveyQuestion['kind'])}>
                  {KINDS.map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Question" htmlFor="question-prompt">
                <input id="question-prompt" className={`${INPUT} w-full`} value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="How was your visit?" />
              </Field>
            </div>
            {needsOptions ? (
              <Field label="Choices" htmlFor="question-options" hint="One per line; at least two.">
                <textarea id="question-options" className={`${INPUT} min-h-20 w-full`} value={options} onChange={(event) => setOptions(event.target.value)} />
              </Field>
            ) : null}
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input type="checkbox" checked={required} onChange={(event) => setRequired(event.target.checked)} /> An answer is required
            </label>
            <Button variant="primary" disabled={add.isPending || !prompt.trim()} onClick={() => add.mutate()}>
              Add question
            </Button>
          </div>
        ) : null}
      </Panel>

      {results.data && results.data.answered > 0 ? (
        <Panel title="Results" description={`${results.data.answered} of ${results.data.invited} asked. Answers are recorded against the ${dateWords().one} and carry no name.`}>
          <Table headers={[`${dateWords().One}`, 'Responses', 'Average rating']}>
            {results.data.by_performance.map((row) => (
              <tr key={row.performance_id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">{inZone(row.starts_at, row.timezone)}</td>
                <td className="tabular px-5 py-3 text-sm">{row.responses}</td>
                <td className="tabular px-5 py-3 text-sm">{row.average_rating ?? '—'}</td>
              </tr>
            ))}
          </Table>
          <div className="space-y-5 border-t border-line px-5 py-4">
            {results.data.questions.map((question) => (
              <div key={question.id}>
                <p className="text-sm font-semibold text-ink-900">
                  {question.prompt}
                  <span className="ml-2 text-xs font-normal text-ink-400">
                    {question.answered} answered{question.average_rating !== null ? ` · average ${question.average_rating}` : ''}
                  </span>
                </p>
                {question.counts.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {question.counts.map((row) => {
                      const most = Math.max(1, ...question.counts.map((other) => other.count));
                      return (
                        <li key={row.label} className="flex items-center gap-2 text-xs">
                          <span className="w-40 shrink-0 text-ink-600">{row.label}</span>
                          <span className="h-3 bg-accent" style={{ width: `${(row.count / most) * 60}%` }} />
                          <span className="tabular text-ink-400">{row.count}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {question.text_answers.length > 0 ? (
                  <ul className="mt-2 space-y-2">
                    {question.text_answers.map((row, index) => (
                      <li key={`${row.submitted_at}-${index}`} className="border-l-2 border-line pl-3 text-sm text-ink-700 italic">
                        {row.text}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </Panel>
      ) : null}
    </>
  );
}
